import { useState } from "react";
import { MapPin, Mail, Phone, Clock, Send, CheckCircle2 } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { useSEO } from "@/hooks/useSEO";
import { useContact } from "@/context/ContactContext";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

export default function Contact() {
  useSEO({
    title: "Contact Us — Renovation Contractor Singapore",
    description: "Contact WAAN METAL PTE. LTD. in Singapore for renovation, fit-out, interior design and metal fabrication enquiries.",
  });
  const contact = useContact();
  const [form, setForm] = useState({ name: "", email: "", phone: "", description: "", consent: false });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide consent to be contacted."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", {
        project_type: "General Enquiry",
        services: [],
        description: form.description,
        name: form.name,
        email: form.email,
        phone: form.phone,
        preferred_contact: "Email",
        consent: form.consent,
      });
      setSent(data.reference);
      trackEvent("contact_submit", { source: "contact_page" });
      toast.success(`Enquiry received — ${data.reference}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="bg-[#0A1128] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-3">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Contact us</h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg">Reach out for a consultation or quotation. We serve residential, commercial and industrial clients across Singapore.</p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Company details</h2>
            <ul className="mt-6 space-y-5">
              <li className="flex gap-4"><span className="w-11 h-11 rounded-xl bg-[#B87333]/10 flex items-center justify-center text-[#B87333] shrink-0"><MapPin size={20} /></span><div><p className="font-semibold text-slate-900">Address</p><p className="text-slate-600">{contact.address}</p></div></li>
              <li className="flex gap-4"><span className="w-11 h-11 rounded-xl bg-[#B87333]/10 flex items-center justify-center text-[#B87333] shrink-0"><Mail size={20} /></span><div><p className="font-semibold text-slate-900">Email</p><a href={`mailto:${contact.email}`} className="text-slate-600 hover:text-[#B87333]">{contact.email}</a></div></li>
              {contact.phone && <li className="flex gap-4"><span className="w-11 h-11 rounded-xl bg-[#B87333]/10 flex items-center justify-center text-[#B87333] shrink-0"><Phone size={20} /></span><div><p className="font-semibold text-slate-900">Phone</p><a href={`tel:+${(contact.phone || "").replace(/[^0-9]/g, "")}`} data-testid="contact-phone-link" className="text-slate-600 hover:text-[#B87333]">{contact.phone}</a></div></li>}
              <li className="flex gap-4"><span className="w-11 h-11 rounded-xl bg-[#B87333]/10 flex items-center justify-center text-[#B87333] shrink-0"><Clock size={20} /></span><div><p className="font-semibold text-slate-900">Business Hours</p><p className="text-slate-600">{contact.business_hours}</p></div></li>
            </ul>
            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 h-64">
              <iframe
                title="WAAN METAL location"
                width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(contact.address)}&output=embed`}
              />
            </div>
          </div>

          <div>
            {sent ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-10 text-center" data-testid="contact-success">
                <CheckCircle2 size={44} className="mx-auto text-[#B87333]" />
                <h3 className="mt-4 text-2xl font-bold text-slate-900">Message received</h3>
                <p className="mt-2 text-slate-600">Your reference is <span className="font-semibold text-slate-900">{sent}</span>. Our team will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl bg-slate-50 border border-slate-200 p-8 space-y-5" data-testid="contact-form">
                <h2 className="text-2xl font-bold text-slate-900">Send us a message</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="contact-name" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none" />
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="contact-phone" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none" />
                </div>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="contact-email" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none" />
                <textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="How can we help?" data-testid="contact-message" className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none resize-none" />
                <label className="flex gap-3 text-sm text-slate-600">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="contact-consent" className="mt-1 accent-[#B87333]" />
                  <span>I consent to WAAN METAL collecting and using my details to respond to this enquiry, in line with the Privacy Policy.</span>
                </label>
                <button type="submit" disabled={busy} data-testid="contact-submit" className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold hover:-translate-y-0.5 transition-all disabled:opacity-60">
                  {busy ? "Sending…" : <>Send Message <Send size={17} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
